/* dl-lightbox.js — design—light PDP · IN-COLUMN photo viewer
   Click any product photo -> the gallery COLUMN darkens and shows that one photo
   whole, centred, pinned to the viewport. Arrows / arrow keys / swipe to move,
   click-drag-pinch-wheel to zoom, Esc / scrim / ✕ to close.

   BINDS TO (product template, inside <div class="dl-scope">):
     .dl-scope .galwrap .gal .sh img     every gallery cell image (incl. .sh.over)
   Adds NOTHING to the page until the first click: the overlay is built lazily and
   appended to <body> (never inside .dl-scope, never inside .galwrap).

   ROLLBACK: remove the two tags from snippets/dl-assets.liquid.
   (dl-lightbox.js + dl-lightbox.css). Nothing else in the theme references this file;
   deleting the tags restores the previous behaviour exactly.

   ── 2026-08-17 REWORK: full-screen -> column-scoped. READ BEFORE EDITING ─────────
   The owner keeps the photo GRID as the default and only wants the expanded photo
   to take over the space the grid already occupies. Three things follow, and all
   three are deliberate reversals of how this file used to work:

     A. FOOTPRINT. The overlay is position:fixed top:0/bottom:0 (full viewport
        height) but its left edge and width are measured from .galwrap and pushed
        into CSS as --dllb-l / --dllb-w by syncBox(). The buy box column is never
        covered and never dimmed: it stays fully LIT and readable while the viewer
        is open. syncBox() re-runs on resize, orientationchange, a ResizeObserver
        on .galwrap and a rAF-throttled scroll, because dl-pdp-wide.css moves the
        column split at 1600px and .galwrap is 100% wide below 820px.
        Because the overlay is the full viewport height, once the page is scrolled
        it also darkens whatever else happens to sit under the gallery column at
        that moment — the related-products rail, the spec table. That is the
        literal consequence of "fills the visible column and follows scrolling",
        which is the behaviour the owner picked (2026-08-17), and closing is one
        Esc / one click away. It is NOT a bug. Do not "fix" it by clipping the
        overlay to .galwrap's height — that reintroduces the half-cut-off tall
        photo this whole rework exists to solve.
     B. NO BODY SCROLL LOCK. The old lock() / unlock() pair (position:fixed on
        <body> + scroll restore) is GONE. The page must keep scrolling behind the
        viewer so a tall photo is never half cut off; because the overlay is
        fixed, the photo stays put while the page moves underneath. Do not
        reintroduce the lock — it silently breaks requirement (A) too, since
        position:fixed on <body> reflows .galwrap and its rect changes.
        Only CLICKS outside the overlay are intercepted (see D). Scrolling —
        wheel over the right column, keyboard, scrollbar — is untouched.
     C. NO THUMBNAIL STRIP, and no focus trap. Arrows + ArrowLeft/ArrowRight +
        swipe are the whole navigation model. A focus trap would fight (D): the
        rest of the page stays reachable, so Tab is left alone and the dialog is
        NOT aria-modal. On open we focus the OVERLAY CONTAINER, not the ✕ —
        Chromium paints :focus-visible on a programmatic focus from a cold page,
        and a red ring on the close button read as an error state on a shop whose
        only red is the brand colour (review 2026-08-17). .dllb has tabindex="-1"
        and outline:none in the CSS; Esc / arrows are bound on document so they
        work no matter where focus sits.
     D. A CLICK ANYWHERE OUTSIDE THE OVERLAY DISMISSES, AND IS SWALLOWED.
        This REVERSES the first cut of this rework, which left the right-hand
        column clickable while the viewer was open. Owner's call, 2026-08-17:
        "even the buy box/description space please". Clicking the buy box, a
        variant swatch, the quantity steppers, the description, the header nav or
        the rail below now closes the viewer and does NOTHING ELSE. The first
        click dismisses, the second click does the thing.
        The swallow is the whole point and is not optional: this is a shop. A
        customer batting a photo away must not add to cart, must not silently
        switch to the Ø 45 cm variant at a different price, must not navigate off
        the product page. Passing the click through would make every dismissal a
        coin flip on the customer's basket. See outsideClick() — it runs in the
        CAPTURE phase on document, before the event can reach its target, and
        calls preventDefault + stopPropagation + stopImmediatePropagation.
        Verified 2026-08-17: nothing in this theme's PDP acts on mousedown or
        pointerdown (dl-product.js binds only "click", "change", "resize",
        "scroll"), so intercepting "click" alone is sufficient — and leaving
        mousedown alone is deliberate, because swallowing it would kill
        scroll-start on touch and text selection on desktop.
     D. The focus target on open is the CONTAINER, not the ✕ — see openFrom().
     E. CLOSE CONTRACT — "clicking anywhere except the picture should close the
        viewer" (owner, 2026-08-17). Photo zooms, prev/next navigate, everything
        else in the overlay closes; a drag/pan/swipe release never closes. It is a
        DEFAULT, not an allow-list of elements. Full rules and ordering are on
        onOverlayClick(); the matrix that verified it is in the deploy note.

   NOT A BUG — the scrim over the rail. Because the overlay is top:0/bottom:0 and
   the page keeps scrolling underneath, scrolling down with the viewer open
   darkens whatever has moved into that column: the "Od stejného výrobce" rail,
   the spec table, the footer. That is the literal consequence of "fills the
   visible column and follows scrolling", which is what the owner chose after
   seeing it (2026-08-17), and closing is one Esc / one tap away. Do NOT "fix"
   this by clipping the overlay to .galwrap's vertical extent — that brings back
   the half-cut-off tall photo this rework exists to remove.

   ── WHY THE OTHER ODD BITS ARE THERE — do not "clean these up" ──────────────────
   1. img.getAttribute("src"), never img.currentSrc / naturalWidth.
      The cells past the cap (.sh.over) are display:none above 820px and the imgs are
      loading="lazy", so the browser has never fetched them: currentSrc is "" and
      naturalWidth is 0. The attribute is always right. This is also why every
      photo is reachable from the viewer even though the grid hides #8 and #9 —
      we read the DOM, we never un-hide a grid cell.
   2. sized(src, 2400).
      The markup's srcset tops out at 1600w with sizes:40vw, so the browser picked a
      small candidate — useless enlarged. Shopify's CDN honours a width= query
      param, so we rewrite it (and append it correctly when the URL has none).
   3. No stopPropagation() on the gallery click.
      dl-product.js has a bubbling listener on .gal that moves the .sh.on highlight
      (variant <-> photo sync). Killing the bubble breaks that sync. We listen on
      document, read only, and never preventDefault.
   4. Tap-vs-swipe slop (TAP_SLOP).
      Below 820px .gal is overflow-x:auto + scroll-snap, and a click can fire at the
      end of a swipe. We measure pointer travel from pointerdown and ignore the click
      if it moved 10px or more, so swiping the carousel never opens the viewer.
   5. .galwrap is never hidden or detached.
      dl-product.js has an IntersectionObserver on it that slides the mobile .pbar in
      when it leaves the viewport; hiding it would strand that bar behind the overlay.
      It is also the element we measure our own footprint from.
   6. Escape is bound on document in the CAPTURE phase and stops propagation ONLY
      while the overlay is open. dl-search.js binds a bubbling document keydown that
      owns Escape and "/". Capturing first (and only when open) closes the viewer
      without ever fighting the search takeover; when we are closed we are inert.
   7. z-index 10090. Highest on the site today: .dlk-toast 10080, .dlk.shm 10070,
      .dlk-dwr 10060, .dlk-ovl 10050 (dl-cart.css), .dls 10002 (dl-search.js),
      .dl-header-b-section 10000, .navx-drawer 9999, .pbar 40, .sticky-atc-bar 29
      (section-sticky-atc-bar.css). 10090 clears all of them with room to spare.
   8. Visibility is toggled with the `hidden` property — dl-tokens.css enforces
      [hidden]{display:none!important}, which is the house idiom.
   9. Wheel only zooms when the pointer is over the PHOTO (or we are already
      zoomed). Everywhere else in the column the wheel is left alone so the page
      can still scroll — which is the point of dropping the body lock.
  10. No dependency. The base theme ships PhotoSwipe but does not load it on the PDP,
      and ~100 KB for this is not acceptable. Everything here is vanilla.
*/
(function () {
  "use strict";
  if (window.__dlLightbox) return;
  window.__dlLightbox = true;

  var D = document;
  var WRAP_SEL = ".dl-scope .galwrap";
  var IMG_SEL = ".dl-scope .galwrap .gal .sh img";
  var BIG = 2400;        /* enlarged render width asked of the Shopify CDN */
  var TAP_SLOP = 10;     /* px of pointer travel that turns a tap into a swipe */
  var ZOOM = 2.5;        /* click / double-tap zoom factor */
  var ZOOM_MAX = 6;
  var TXT = {
    dialog: "Fotogalerie produktu",
    close: "Zavřít",
    prev: "Předchozí fotografie",
    next: "Další fotografie"
  };

  var lb = null, els = null;
  var items = [], idx = 0;
  var isOpen = false;
  var opener = null;              /* the <img> that was clicked, for focus restore */
  var wrap = null;                /* the .galwrap we are sized against */
  var ro = null, rafId = 0;

  /* zoom / pan state */
  var scale = 1, tx = 0, ty = 0;
  var pts = {}, start = null, pinch = null, dragged = false;
  var lastTap = 0, lastTapX = 0, lastTapY = 0, lastPointerType = "mouse";

  /* pointer travel of the CURRENT gesture, used for constraint 4 */
  var down = null, downLive = false;

  /* ---------- helpers ---------- */

  /* Shopify CDN width rewrite. Handles both "…?v=1&width=1200" and a URL with no
     width param at all (respecting existing ? vs &). Constraint 2. */
  function sized(src, w) {
    if (!src) return "";
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, "$1width=" + w);
    return src + (src.indexOf("?") === -1 ? "?" : "&") + "width=" + w;
  }

  function dist(a, b) { var dx = a.x - b.x, dy = a.y - b.y; return Math.sqrt(dx * dx + dy * dy); }
  function ids() { return Object.keys(pts); }
  function two() { var k = ids(); return [pts[k[0]], pts[k[1]]]; }

  /* Order is DOM order of .gal .sh — data-media is media.position and counts videos
     and 3D models, so it is NOT a contiguous index and must not drive ordering. */
  function collect(gal) {
    var out = [];
    var cells = gal.querySelectorAll(".sh");
    for (var i = 0; i < cells.length; i++) {
      var img = cells[i].querySelector("img");
      if (!img) continue;                       /* product with no media: <div class="sh"></div> */
      var src = img.getAttribute("src");        /* constraint 1 */
      if (!src) continue;
      out.push({ src: src, alt: img.getAttribute("alt") || "", el: img });
    }
    return out;
  }

  /* ---------- footprint (rework note A) ---------- */

  /* Pin the overlay to the gallery column: same left edge, same width, full
     viewport height. Never assume a fraction of the window — .galwrap is 100%
     wide below 820px, ~73% at 1440 and dl-pdp-wide.css shifts the split again
     past 1600px. Measured, not guessed. */
  function syncBox() {
    if (!lb) return;
    var l = 0, w = D.documentElement.clientWidth || window.innerWidth || 0;
    var el = wrap || D.querySelector(WRAP_SEL) || D.querySelector(".galwrap");
    if (el) {
      var r = el.getBoundingClientRect();
      if (r.width > 1) { l = r.left; w = r.width; }
    }
    lb.style.setProperty("--dllb-l", l + "px");
    lb.style.setProperty("--dllb-w", w + "px");
  }

  function syncSoon() {
    if (rafId) return;
    rafId = window.requestAnimationFrame(function () { rafId = 0; if (isOpen) syncBox(); });
  }

  function watch() {
    if (!isOpen) return;
    window.addEventListener("resize", syncSoon);
    window.addEventListener("orientationchange", syncSoon);
    /* the column does not move horizontally when the page scrolls, but a sticky
       header collapsing or a scrollbar appearing can change its width, and this
       is cheap next to being one pixel off. */
    window.addEventListener("scroll", syncSoon, { passive: true });
    if (wrap && "ResizeObserver" in window) {
      ro = new ResizeObserver(syncSoon);
      ro.observe(wrap);
    }
  }
  function unwatch() {
    window.removeEventListener("resize", syncSoon);
    window.removeEventListener("orientationchange", syncSoon);
    window.removeEventListener("scroll", syncSoon);
    if (ro) { try { ro.disconnect(); } catch (e) {} ro = null; }
    if (rafId) { window.cancelAnimationFrame(rafId); rafId = 0; }
  }

  /* ---------- overlay ---------- */

  var SVG_X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  var SVG_L = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"/></svg>';
  var SVG_R = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 5l7 7-7 7"/></svg>';

  function build() {
    lb = D.createElement("div");
    lb.className = "dllb";
    lb.hidden = true;
    lb.setAttribute("role", "dialog");
    /* NOT aria-modal: the rest of the page stays visible, lit and reachable by
       keyboard (notes A / C / D). */
    lb.setAttribute("aria-label", TXT.dialog);
    /* focus target on open — see note C. The ring is killed in dl-lightbox.css. */
    lb.setAttribute("tabindex", "-1");
    /* Focus target on open — see note D. The container, not the ✕. */
    lb.setAttribute("tabindex", "-1");
    lb.innerHTML =
      '<div class="dllb-scrim" data-dllb-scrim></div>' +
      '<div class="dllb-frame" data-dllb-frame>' +
        '<div class="dllb-bar">' +
          '<span class="dllb-count" data-dllb-count></span>' +
          '<button type="button" class="dllb-btn dllb-x" data-dllb-x aria-label="' + TXT.close + '">' + SVG_X + '</button>' +
        '</div>' +
        '<div class="dllb-stage" data-dllb-stage>' +
          '<button type="button" class="dllb-btn dllb-nav dllb-prev" data-dllb-prev aria-label="' + TXT.prev + '">' + SVG_L + '</button>' +
          '<img class="dllb-img" data-dllb-img alt="" draggable="false">' +
          '<button type="button" class="dllb-btn dllb-nav dllb-next" data-dllb-next aria-label="' + TXT.next + '">' + SVG_R + '</button>' +
        '</div>' +
      '</div>';
    D.body.appendChild(lb);

    els = {
      scrim: lb.querySelector("[data-dllb-scrim]"),
      frame: lb.querySelector("[data-dllb-frame]"),
      count: lb.querySelector("[data-dllb-count]"),
      x: lb.querySelector("[data-dllb-x]"),
      stage: lb.querySelector("[data-dllb-stage]"),
      prev: lb.querySelector("[data-dllb-prev]"),
      next: lb.querySelector("[data-dllb-next]"),
      img: lb.querySelector("[data-dllb-img]")
    };

    /* Never upscale past the natural size of what actually arrived.
       FIX 2026-08-17 — this used to write `max-width/max-height: min(100%,Npx)`
       straight onto the element. The percentage half of that never resolved:
       the photo was a GRID ITEM of .dllb-stage, whose height comes from a
       `minmax(0,1fr)` track, and a percentage max-height against an indefinite
       grid area computes to `none`. So a 2400px CDN render drew at 2400px and
       overflowed the whole overlay. The photo is now absolutely positioned (see
       dl-lightbox.css) and the natural size travels as two custom properties;
       the clamping against the stage lives in CSS where it can resolve. */
    els.img.addEventListener("load", function () {
      var n = els.img.naturalWidth, h = els.img.naturalHeight;
      els.img.style.setProperty("--lb-nw", n ? n + "px" : "100%");
      els.img.style.setProperty("--lb-nh", h ? h + "px" : "100%");
      els.stage.classList.remove("busy");
    });

    /* Every fresh press anywhere in the overlay clears the drag flag, in the
       CAPTURE phase so it lands before bindStage()'s own pointerdown. Without
       this, `dragged` left true by a pan would survive into the next click — and
       because the drag guard now runs first in onOverlayClick(), that stale flag
       would swallow a genuine press on ✕ (which is in the bar, outside the stage,
       so bindStage never sees it) and leave the customer unable to close. */
    lb.addEventListener("pointerdown", function () { dragged = false; }, true);
    lb.addEventListener("click", onOverlayClick);
    bindStage();
    return lb;
  }

  /* THE POINTER-CAPTURE RETARGETING TRAP — read this before touching any click
     handling in here. It cost two separate bugs on 2026-08-17.

     bindStage() calls setPointerCapture() on .dllb-stage for the first pointer so
     that a pan/pinch keeps tracking outside the stage. Chromium then retargets the
     compatibility mouse events to the capture element, so `mouseup` fires on
     .dllb-stage no matter what was actually under the cursor — and a `click`'s
     target is the nearest common ancestor of the mousedown and mouseup targets.
     Result: EVERY mouse click inside the stage arrived as e.target === .dllb-stage.

       • click the photo  -> not e.target===img -> fell through to "click on the
         scrim" -> the viewer CLOSED instead of zooming.
       • click prev/next  -> closest("button") === null -> same branch -> the
         viewer CLOSED instead of navigating. (Verified in the harness: clicking
         ▸ at 1920 hid the overlay and left the counter on 1 / 9.)

     Touch does not behave the same way — the gesture recogniser hit-tests the tap
     afresh, so there e.target is the real element. Anything that trusts e.target
     inside the stage is therefore broken on desktop and fine on mobile, which is
     exactly how it slipped through before.

     hitTarget() re-resolves the real element under the pointer for genuine pointer
     clicks (detail > 0). Keyboard/synthetic clicks (detail === 0, no coordinates)
     keep e.target, which is already correct for them. */
  function hitTarget(e) {
    var t = e.target;
    if (!e.detail) return t;                       /* keyboard / .click() */
    if (t !== els.stage && t !== els.frame && t !== els.scrim) return t;
    var hit = D.elementFromPoint(e.clientX, e.clientY);
    return (hit && lb.contains(hit)) ? hit : t;
  }

  /* Is this client point over the photo AS PAINTED — i.e. over pixels the customer
     can actually see?

     The intersection with the stage is load-bearing, not tidiness. .dllb-stage is
     overflow:hidden, but a zoomed photo's getBoundingClientRect() is the FULL
     transformed box, which at 2.5x runs off the top and bottom of the stage and
     straight under the top bar (measured at 1920: img top -425, bottom 2007, stage
     63..1080). Without the clamp, a click on the bar — or on the counter — while
     zoomed would test as "on the photo" and toggle zoom instead of closing, which
     is precisely the dead zone the close contract exists to remove. */
  function onPhoto(x, y) {
    if (!els || !els.img || !els.img.getAttribute("src")) return false;
    if (typeof x !== "number" || typeof y !== "number") return false;
    var r = els.img.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    var s = els.stage.getBoundingClientRect();
    var l = Math.max(r.left, s.left), t = Math.max(r.top, s.top);
    var rt = Math.min(r.right, s.right), bt = Math.min(r.bottom, s.bottom);
    if (rt <= l || bt <= t) return false;         /* photo entirely clipped away */
    return x >= l && x <= rt && y >= t && y <= bt;
  }

  /* THE CLOSE CONTRACT (owner's wording: "clicking anywhere except the picture
     should close the viewer"). Evaluated strictly in this order:

       1. a drag / pan / swipe release -> nothing at all. Never closes, never
                           navigates, never zooms.
       2. prev / next   -> navigate. Never closes.
       3. ✕             -> close.
       4. the photo     -> zoom (mouse); on touch, nothing here — double-tap in
                           endPointer owns zoom. Never closes.
       5. ANYTHING ELSE inside the overlay -> close. The top bar, the whitespace
                           beside the counter, the counter text itself, the
                           letterboxing around the photo, the frame padding. There
                           are deliberately NO dead zones left.

     Rule 5 is a default, not a list of elements. It used to be
     `if (t === scrim || t === frame || t === stage) close()`, which meant every
     other element — the bar, the counter — silently swallowed the click. Do not
     turn it back into an allow-list: any element added to this overlay later would
     become a new dead zone the moment it shipped.

     WHY THE DRAG GUARD IS FIRST, above the buttons. The prev/next arrows sit on
     top of the photo, so a swipe very often RELEASES over one of them — and the
     arrow you land on is the opposite of the one your swipe means (swipe left,
     finish near the left edge, land on ‹). With the guard below the button branch
     the swipe advanced one photo and the phantom arrow click immediately took it
     back, so a swipe that ended near an edge looked like nothing happened.
     Measured in the harness at 400px: swipe left released at x=8 -> 5/9 -> 6/9
     -> 5/9. The guard has to run before anything can act on the click.
     This is only safe because resetDragged() below clears the flag on every fresh
     press anywhere in the overlay — otherwise a stale `dragged` from an earlier
     pan would swallow the next genuine click on ✕ and strand the customer. */
  function onOverlayClick(e) {
    /* t, never e.target — see the pointer-capture trap above hitTarget(). */
    var t = hitTarget(e);
    if (dragged) return;                                     /* 1 */
    var b = t.closest ? t.closest("button") : null;
    if (b) {
      if (b.hasAttribute("data-dllb-prev")) return go(-1);   /* 2 */
      if (b.hasAttribute("data-dllb-next")) return go(1);    /* 2 */
      return close();                                        /* 3, and any button added later */
    }
    /* 4 — belt and braces on top of hitTarget(): test the pointer against the
       photo's painted box too, so this does not depend on hit-testing at all.
       Touch does not zoom here; double-tap in endPointer owns that. */
    if (onPhoto(e.clientX, e.clientY) || t === els.img) {
      if (lastPointerType !== "touch") toggleZoom(e.clientX, e.clientY);
      return;
    }
    close();                                                 /* 5 */
  }

  /* ---------- rendering ---------- */

  function preload(i) {
    var n = [i - 1, i + 1];
    for (var k = 0; k < n.length; k++) {
      var j = n[k];
      if (j < 0 || j >= items.length) continue;
      var im = new Image();
      im.src = sized(items[j].src, BIG);   /* neighbours only — never the whole set */
    }
  }

  function show(i) {
    if (!items.length) return;
    if (i < 0) i = items.length - 1;
    if (i > items.length - 1) i = 0;
    idx = i;
    resetZoom();
    var it = items[idx];
    els.stage.classList.add("busy");
    els.img.src = sized(it.src, BIG);
    els.img.alt = it.alt;
    els.img.style.removeProperty("--lb-nw");
    els.img.style.removeProperty("--lb-nh");
    els.count.textContent = (idx + 1) + " / " + items.length;
    preload(idx);
  }

  function go(d) { show(idx + d); }

  /* ---------- zoom / pan ---------- */

  function applyT() {
    els.img.style.transform = scale === 1 ? "" : "translate(" + tx + "px," + ty + "px) scale(" + scale + ")";
    els.img.classList.toggle("zoomed", scale > 1);
  }
  function resetZoom() { scale = 1; tx = 0; ty = 0; pinch = null; if (els) applyT(); }
  function clamp() {
    var r = els.stage.getBoundingClientRect();
    var mx = Math.max(0, (els.img.offsetWidth * scale - r.width) / 2);
    var my = Math.max(0, (els.img.offsetHeight * scale - r.height) / 2);
    tx = Math.min(mx, Math.max(-mx, tx));
    ty = Math.min(my, Math.max(-my, ty));
  }
  /* Zoom to s keeping the client point (px,py) pinned. CSS applies
     translate(t) scale(s) as p = centre + d*s + t, so d = (p - centre)/s0. */
  function zoomTo(s, px, py) {
    s = Math.max(1, Math.min(ZOOM_MAX, s));
    var r = els.img.getBoundingClientRect();
    var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    var dx = scale ? (px - cx) / scale : 0, dy = scale ? (py - cy) / scale : 0;
    var bx = cx - tx, by = cy - ty;         /* untranslated centre */
    scale = s;
    tx = px - dx * s - bx;
    ty = py - dy * s - by;
    if (scale === 1) { tx = 0; ty = 0; }
    clamp();
    applyT();
  }
  function toggleZoom(px, py) {
    if (scale > 1) { resetZoom(); return; }
    var r = els.stage.getBoundingClientRect();
    zoomTo(ZOOM, px == null ? r.left + r.width / 2 : px, py == null ? r.top + r.height / 2 : py);
  }

  function bindStage() {
    var st = els.stage;
    st.addEventListener("pointerdown", function (e) {
      lastPointerType = e.pointerType || "mouse";
      /* The prev/next buttons live INSIDE the stage. Never start a gesture — and
         above all never take pointer capture — for a press that begins on one of
         them: capture is what corrupts the click target (see hitTarget()), and a
         captured button also loses its :active feedback. */
      if (e.target && e.target.closest && e.target.closest("button")) return;
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      var n = ids().length;
      if (n === 1) {
        start = { x: e.clientX, y: e.clientY, tx: tx, ty: ty, t: Date.now() };
        dragged = false;
        els.img.classList.add("live");
        if (st.setPointerCapture) { try { st.setPointerCapture(e.pointerId); } catch (err) {} }
      } else if (n === 2) {
        var p = two();
        pinch = { d: dist(p[0], p[1]), s: scale, mx: (p[0].x + p[1].x) / 2, my: (p[0].y + p[1].y) / 2 };
        start = null;
        dragged = true;
      }
    });
    st.addEventListener("pointermove", function (e) {
      if (!pts[e.pointerId]) return;
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (ids().length >= 2 && pinch) {
        var p = two();
        if (pinch.d > 0) zoomTo(pinch.s * (dist(p[0], p[1]) / pinch.d), pinch.mx, pinch.my);
        e.preventDefault();
        return;
      }
      if (!start) return;
      var dx = e.clientX - start.x, dy = e.clientY - start.y;
      if (Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP) dragged = true;
      if (scale > 1) { tx = start.tx + dx; ty = start.ty + dy; clamp(); applyT(); e.preventDefault(); }
    }, { passive: false });
    st.addEventListener("pointerup", endPointer);
    st.addEventListener("pointercancel", endPointer);
    /* Wheel zoom — constraint 9. Only over the photo itself, or once zoomed.
       Anywhere else in the column the wheel scrolls the page as usual, which is
       the whole reason the body lock was removed. */
    st.addEventListener("wheel", function (e) {
      if (scale === 1 && e.target !== els.img) return;
      e.preventDefault();
      var f = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0015));
      zoomTo(scale * f, e.clientX, e.clientY);
    }, { passive: false });
  }

  function endPointer(e) {
    var had = pts[e.pointerId];
    delete pts[e.pointerId];
    if (ids().length < 2) pinch = null;
    if (!had) return;
    els.img.classList.remove("live");
    if (start && ids().length === 0) {
      var dx = e.clientX - start.x, dy = e.clientY - start.y, dt = Date.now() - start.t;
      /* Swipe-to-navigate only while un-zoomed, otherwise panning is impossible. */
      if (scale === 1 && Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.4 && dt < 900) {
        go(dx < 0 ? 1 : -1);
      } else if (!dragged && (e.pointerType === "touch" || e.pointerType === "pen")) {
        var now = Date.now();
        if (now - lastTap < 320 && Math.abs(e.clientX - lastTapX) < 28 && Math.abs(e.clientY - lastTapY) < 28) {
          toggleZoom(e.clientX, e.clientY);
          lastTap = 0;
        } else { lastTap = now; lastTapX = e.clientX; lastTapY = e.clientY; }
      }
      start = null;
    }
    if (scale === 1 && (tx || ty)) { tx = 0; ty = 0; applyT(); }
  }

  /* ---------- open / close ---------- */

  function openFrom(img) {
    var gal = img.closest(".gal");
    if (!gal) return;
    var list = collect(gal);
    if (!list.length) return;
    items = list;
    var at = 0;
    for (var i = 0; i < items.length; i++) if (items[i].el === img) { at = i; break; }
    opener = img;
    wrap = img.closest(".galwrap") || D.querySelector(WRAP_SEL);
    if (!lb) build();
    var one = items.length < 2;
    els.prev.hidden = one;
    els.next.hidden = one;
    lb.hidden = false;
    isOpen = true;
    syncBox();                 /* measure AFTER unhiding — layout is settled */
    watch();
    show(at);
    /* Note D — focus the CONTAINER, never the ✕.
       Focusing the close button drew Chromium's :focus-visible ring on it the
       moment the viewer opened (programmatic focus from a cold page counts as
       keyboard-ish), and on this store the only red is the brand colour, so a
       red ring around ✕ read as an error state. Focusing .dllb (tabindex="-1",
       outline:none in the CSS) still puts the keyboard inside the viewer — the
       container precedes its children, so the first Tab lands on ✕ — and Esc is
       bound on document anyway, so nothing about the keyboard path changes.
       preventScroll matters now that the page is not locked: focusing anything
       inside a fixed overlay must not yank the page underneath. */
    if (lb.focus) { try { lb.focus({ preventScroll: true }); } catch (err) { lb.focus(); } }
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    unwatch();
    resetZoom();
    lb.hidden = true;
    els.img.removeAttribute("src");
    pts = {}; start = null; dragged = false;
    wrap = null;
    if (opener) {
      /* an <img> is not focusable by default — give it a programmatic focus target
         so keyboard users land back where they were. */
      if (!opener.hasAttribute("tabindex")) opener.setAttribute("tabindex", "-1");
      if (opener.focus) { try { opener.focus({ preventScroll: true }); } catch (err) { opener.focus(); } }
      opener = null;
    }
  }

  /* ---------- page bindings ---------- */

  /* Constraint 4: measure the travel of the whole gesture, not just down->click,
     because a scroll-snap swipe can end back where it started. */
  D.addEventListener("pointerdown", function (e) {
    down = { x: e.clientX, y: e.clientY, moved: 0 };
    downLive = true;
  }, true);
  D.addEventListener("pointermove", function (e) {
    if (!downLive || !down) return;
    var d = Math.abs(e.clientX - down.x) + Math.abs(e.clientY - down.y);
    if (d > down.moved) down.moved = d;
  }, { capture: true, passive: true });
  D.addEventListener("pointerup", function () { downLive = false; }, true);
  D.addEventListener("pointercancel", function () { downLive = false; down = null; }, true);

  /* Constraint 3: plain bubbling listener, no stopPropagation, no preventDefault —
     dl-product.js's .gal listener still gets the click and moves .sh.on. */
  D.addEventListener("click", function (e) {
    if (isOpen) return;
    if (!e.target || !e.target.closest) return;
    var img = e.target.closest(IMG_SEL);
    if (!img) return;
    if (e.detail > 0 && down && down.moved >= TAP_SLOP) return;   /* that was a swipe */
    openFrom(img);
  });

  /* NOTE D — a click ANYWHERE OUTSIDE the overlay dismisses it, and that click is
     swallowed so it does not also do whatever it was pointing at.

     Owner, 2026-08-17: "even the buy box/description space please." First click
     dismisses, second click does the thing.

     WHY IT IS SWALLOWED, and why that is not negotiable: this is a shop. Without
     preventDefault the dismissing click lands on whatever is underneath — the
     black PŘIDAT DO KOŠÍKU button, a size swatch that changes the price, a nav
     link that leaves the product page. Measured before this handler existed: a
     click on "Ø 45 cm" while the viewer was open moved the price from 12 490 Kč
     to 16 490 Kč. Every dismissal would have been a coin flip on the customer's
     basket. Do NOT "improve" this into a pass-through.

     WHY CAPTURE PHASE, ON document. It runs before the event can reach its
     target, so preventDefault + stopPropagation + stopImmediatePropagation
     together stop the activation behaviour (link navigation, form submit),
     dl-product.js's own document/element listeners, and everything in between.
     It is also why it cannot double-fire with onOverlayClick(): it returns
     immediately for anything the overlay contains.

     WHY ONLY `click`, and deliberately NOT pointerdown / mousedown. Swallowing a
     press would kill scroll-start on touch and text selection on desktop, for no
     gain: verified 2026-08-17 that nothing on this PDP acts on a press —
     dl-product.js binds only "click", "change", "resize" and "scroll", and every
     control in the buy box (variant buttons, qty steppers, submit) is a <button>
     that activates on click. If a future control ever reacts on mousedown, this
     is the place to extend — but measure it first, do not add it speculatively.

     SCROLLING IS NEVER TOUCHED. No wheel, scroll or key handler here; the page
     keeps scrolling behind the viewer, which is the point of having no body lock.

     The TAP_SLOP guard keeps a text-selection drag, a scroll flick or a swipe
     that happens to end outside from counting as a dismissal. */
  function outsideClick(e) {
    if (!isOpen || !lb) return;
    if (e.target && lb.contains(e.target)) return;   /* onOverlayClick owns those */
    if (e.detail > 0 && down && down.moved >= TAP_SLOP) return;  /* drag, not a tap */
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    close();
  }
  D.addEventListener("click", outsideClick, true);

  /* Constraint 6: capture phase, and we only swallow keys while we are open.
     No Tab handling — the rest of the page stays reachable on purpose (note C);
     a click out there dismisses first (note D), a Tab out there does not. */
  D.addEventListener("keydown", function (e) {
    if (!isOpen) return;
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); return; }
    if (e.key === "ArrowLeft") { e.preventDefault(); e.stopPropagation(); go(-1); return; }
    if (e.key === "ArrowRight") { e.preventDefault(); e.stopPropagation(); go(1); return; }
  }, true);

  window.DLLightbox = {
    open: function (i) {
      var img = D.querySelector(IMG_SEL);
      if (!img) return;
      openFrom(img);
      if (typeof i === "number") show(i);
    },
    close: close,
    sync: syncBox
  };
})();
